import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from '@/components/kibo-ui/dropzone';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useUploadImage } from '@/features/upload/api/upload.hook';
import {
  useCreateVenuePhoto,
  useDeleteVenuePhoto,
  useListVenuePhotos,
  useReorderVenuePhotos,
  useSetCoverPhoto,
} from '@/features/venue/api';
import { cn } from '@/lib/utils';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ImageIcon,
  Loader2Icon,
  MonitorIcon,
  PencilIcon,
  SmartphoneIcon,
  StarIcon,
  TabletIcon,
  Trash2Icon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type VenuePhoto = ReturnType<typeof useListVenuePhotos>['photos'][number];

/**
 * A photo shown whole, at its own aspect ratio, inside a fixed tile: the
 * picture is contained and a blurred copy of itself fills the rest, so a
 * portrait headshot is not cropped to a forehead and the tile never shows
 * grey bars. The public venue page frames photos the same way.
 */
function TilePhoto({ photo }: { photo: VenuePhoto }) {
  return (
    <>
      <img
        src={photo.url}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-[1.3] object-cover blur-2xl brightness-[0.92] saturate-[1.3]"
      />
      <img
        src={photo.url}
        alt={photo.caption ?? 'Venue photo'}
        className="relative h-full w-full object-contain [filter:drop-shadow(0_12px_28px_rgb(16_24_40/0.24))]"
      />
    </>
  );
}

interface VenuePhotoManagerProps {
  locationId: string;
}

/**
 * Gallery manager for one venue. The photos render as the same hero grid shown
 * on the public page — one large lead image, two stacked to its right, and a
 * "See all images" badge that opens the full manager: upload (presigned →
 * public bucket), reorder (up/down), set the cover, and delete. Captions are
 * set at upload time — the backend exposes no photo-caption update endpoint, so
 * existing captions are shown but not editable inline.
 */
type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

const PREVIEW_DEVICES: {
  value: PreviewDevice;
  label: string;
  icon: typeof MonitorIcon;
}[] = [
  { value: 'desktop', label: 'Web', icon: MonitorIcon },
  { value: 'tablet', label: 'Tablet', icon: TabletIcon },
  { value: 'mobile', label: 'Mobile', icon: SmartphoneIcon },
];

/**
 * The frame each device is previewed in: the page's own width for web, and a
 * device-width screen for tablet (768px less the page's padding) and phone.
 */
const DEVICE_FRAME: Record<PreviewDevice, string> = {
  desktop: 'w-full',
  tablet:
    'w-full max-w-[744px] rounded-[26px] border bg-background p-5 shadow-sm ring-[10px] ring-muted',
  mobile:
    'w-[375px] max-w-full rounded-[34px] border bg-background p-4 shadow-sm ring-[10px] ring-muted',
};

/**
 * The public venue page's photo mosaic, at the same size and in the same
 * shape (apps/marketing-astro venue-gallery + venue-experience.css): one tile
 * for one photo, two side by side for two, otherwise a large lead with two
 * stacked beside it — under it on a phone. What the clinic sees here is what
 * customers see there, on whichever device they pick, plus the pencil that
 * says it can be edited.
 *
 * The phone layout is chosen by the device switch, not the browser width, so
 * it can be previewed from a desktop. Web and tablet still fall back to the
 * phone layout when the dashboard itself is on a phone.
 */
function VenuePhotoPreview({
  photos,
  onEdit,
}: {
  photos: VenuePhoto[];
  onEdit: () => void;
}) {
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const phone = device === 'mobile';
  const tiles = photos.slice(0, 3);
  const layout =
    tiles.length === 1 ? 'single' : tiles.length === 2 ? 'pair' : 'mosaic';
  const countLabel = `${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`;

  return (
    <div className="space-y-3">
      <div className="hidden items-center justify-between gap-3 sm:flex">
        <p className="font-medium text-muted-foreground text-sm">Preview</p>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={device}
          onValueChange={(value) => {
            if (value) setDevice(value as PreviewDevice);
          }}
          aria-label="Preview device"
        >
          {PREVIEW_DEVICES.map(({ value, label, icon: Icon }) => (
            <ToggleGroupItem
              key={value}
              value={value}
              aria-label={`${label} preview`}
              title={`${label} preview`}
              className="px-2.5"
            >
              <Icon className="size-4" />
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div
        className={cn(
          'mx-auto transition-[max-width,width,padding] duration-300 ease-out',
          DEVICE_FRAME[device]
        )}
      >
        <div
          className={cn(
            'relative grid gap-2',
            layout === 'single' && 'h-[230px] grid-cols-1',
            layout === 'pair' && 'h-[230px] grid-cols-2',
            layout === 'mosaic' &&
              'h-[308px] grid-cols-2 grid-rows-[minmax(0,1fr)_78px]',
            layout === 'mosaic' &&
              !phone &&
              'sm:h-[360px] sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] sm:grid-rows-2'
          )}
        >
          {tiles.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={onEdit}
              aria-label={`Edit photo ${index + 1} of ${photos.length}`}
              className={cn(
                'relative min-h-0 overflow-hidden rounded-xl bg-muted',
                layout === 'mosaic' &&
                  index === 0 &&
                  (phone
                    ? 'col-span-2'
                    : 'col-span-2 sm:col-span-1 sm:row-span-2')
              )}
            >
              <TilePhoto photo={photo} />
              {index === tiles.length - 1 && !phone && (
                <span className="absolute right-2.5 bottom-2.5 hidden items-center gap-1.5 rounded-lg border border-black/5 bg-white/95 px-3 py-2 font-medium text-foreground text-xs shadow-sm sm:inline-flex">
                  <ImageIcon aria-hidden="true" className="size-3.5" />
                  View {countLabel}
                </span>
              )}
            </button>
          ))}
          {/* Where the public page puts its slideshow control. A sibling of
              the tiles, not inside one: buttons cannot nest. */}
          <button
            type="button"
            onClick={onEdit}
            className="absolute top-2.5 right-2.5 flex items-center gap-1.5 rounded-full bg-background/95 px-3 py-1.5 font-medium text-sm shadow backdrop-blur-sm transition hover:bg-background"
          >
            <PencilIcon className="size-3.5" />
            Edit photos
          </button>
        </div>
        {/* The phone page swaps the corner chip for a link under the photos. */}
        <button
          type="button"
          onClick={onEdit}
          className={cn(
            'mt-1 ml-auto flex min-h-11 items-center gap-2 font-medium text-primary text-sm',
            !phone && 'sm:hidden'
          )}
        >
          <ImageIcon aria-hidden="true" className="size-4" />
          View all {countLabel}
        </button>
      </div>
    </div>
  );
}

export function VenuePhotoManager({ locationId }: VenuePhotoManagerProps) {
  const { photos, isLoading } = useListVenuePhotos(locationId);
  const { createPhotoAsync, isCreating } = useCreateVenuePhoto(locationId);
  const { reorderPhotos, isReordering } = useReorderVenuePhotos(locationId);
  const { setCover, isSettingCover } = useSetCoverPhoto(locationId);
  const { deletePhoto, isDeleting } = useDeleteVenuePhoto(locationId);

  const [caption, setCaption] = useState('');
  const [managerOpen, setManagerOpen] = useState(false);
  const { uploadAsync, isUploading } = useUploadImage({
    purpose: 'profile',
    showToast: false,
  });

  const busy = isReordering || isSettingCover || isDeleting || isCreating;

  const handleDrop = async (files: File[]) => {
    for (const file of files) {
      try {
        const result = await uploadAsync(file);
        await createPhotoAsync({
          url: result.url,
          caption: caption.trim() || null,
        });
      } catch {
        toast.error(`Failed to add ${file.name}`);
      }
    }
    setCaption('');
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= photos.length) return;
    const ids = photos.map((p) => p.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderPhotos(ids);
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading photos…</p>
      ) : photos.length === 0 ? (
        <button
          type="button"
          onClick={() => setManagerOpen(true)}
          className="flex h-36 w-full sm:aspect-[2/1] sm:h-auto flex-col items-center justify-center gap-2 rounded-2xl bg-muted text-muted-foreground transition hover:bg-muted/70"
        >
          <ImageIcon className="size-10" />
          <span className="text-sm">Add photos</span>
        </button>
      ) : (
        <VenuePhotoPreview
          photos={photos}
          onEdit={() => setManagerOpen(true)}
        />
      )}

      <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
        <DialogContent
          // The base dialog caps at `sm:max-w-lg` (512px), which squeezed
          // three cards into ~150px each and spilled their buttons.
          className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle>Venue photos</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption for the next upload (optional)"
              maxLength={200}
            />
            <Dropzone
              accept={{ 'image/*': [] }}
              maxFiles={10}
              multiple
              disabled={isUploading || isCreating}
              onDrop={handleDrop}
              onError={(e) => toast.error(e.message)}
            >
              <DropzoneEmptyState />
              <DropzoneContent />
            </Dropzone>
            {(isUploading || isCreating) && (
              <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
                <Loader2Icon className="size-3 animate-spin" /> Uploading…
              </p>
            )}
          </div>

          {photos.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No photos yet. Upload some above — they appear on your public
              venue page.
            </p>
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(176px,1fr))] gap-3">
              {photos.map((photo, index) => (
                <li
                  key={photo.id}
                  className="flex flex-col overflow-hidden rounded-xl border bg-card"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <TilePhoto photo={photo} />
                    {photo.isCover && (
                      <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-background px-2 py-1 font-medium text-xs shadow">
                        <StarIcon className="size-3 fill-current" /> Cover
                      </span>
                    )}
                    <span className="absolute top-2 right-2 rounded-full bg-background/90 px-2 py-0.5 font-medium text-xs tabular-nums shadow">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-2">
                    {photo.caption && (
                      <p className="line-clamp-2 text-muted-foreground text-xs">
                        {photo.caption}
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant={photo.isCover ? 'secondary' : 'outline'}
                      className="mt-auto w-full"
                      disabled={busy || photo.isCover}
                      onClick={() => setCover(photo.id)}
                    >
                      <StarIcon
                        className={
                          photo.isCover ? 'size-4 fill-current' : 'size-4'
                        }
                      />
                      {photo.isCover ? 'Cover photo' : 'Set as cover'}
                    </Button>
                    <div className="flex items-center justify-between">
                      <div className="flex">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          disabled={busy || index === 0}
                          onClick={() => move(index, -1)}
                          aria-label="Move photo earlier"
                        >
                          <ArrowUpIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          disabled={busy || index === photos.length - 1}
                          onClick={() => move(index, 1)}
                          aria-label="Move photo later"
                        >
                          <ArrowDownIcon className="size-4" />
                        </Button>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        disabled={busy}
                        onClick={() => deletePhoto(photo.id)}
                        aria-label="Delete photo"
                      >
                        <Trash2Icon className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
